import axios from 'axios';
import queryString, { StringifiableRecord } from 'query-string-for-all';
import { useMemo } from 'react';
import { useQueries, useQuery } from 'react-query';

import { CACHE_KEYS } from './config';
import {
  InsightsPlatform,
  Qualification,
  QualificationAnswer,
} from './insightsTypes';
import { api } from './swaggerApi';

export const qualificationList = (countryId: string, languageId: string) =>
  axios.get<{ data: [Qualification] }>(
    `/insights_platform/qualifications?country_id=${countryId}&language_id=${languageId}`
  );
export const getDeveloperQualifications = (
  countryId: string,
  languageId: string
) =>
  axios.get(
    `/insights_platform/developer_qualifications?country_id=${countryId}&language_id=${languageId}`
  );
export const listWithTopics = (countryId: string, languageId: string) =>
  axios.get(
    `/insights_platform/qualification_topics/targeting_qualifications?country_id=${countryId}&language_id=${languageId}`
  );
export const get = (id: string) =>
  axios.get(`/insights_platform/qualifications/${id}`);

export const useGetQualification = (id: string) => {
  return useQuery(
    [CACHE_KEYS.QUALIFICATION, id],
    () => api.insightsPlatform.qualificationsDetail
  );
};

export const useGetQualifications = (
  countryId: string,
  languageId: string,
  profilingOnly?: number
) => {
  return useQuery(
    [CACHE_KEYS.QUALIFICATIONS, countryId, languageId],
    () =>
      api.insightsPlatform.qualificationsList({
        language_id: languageId,
        country_id: countryId,
        profiling_only: profilingOnly,
      }),
    { refetchOnWindowFocus: false }
  );
};

export interface QualificationWithQualificationAnswers extends Qualification {
  attributes: Qualification['attributes'] & {
    qualification_answers: QualificationAnswer[];
  };
}

export const useGetQualificationsWithAnswers = (
  countryId: string,
  languageId: string,
  byQuestionId = false
) => {
  const qualificationsQuery = useGetQualifications(countryId, languageId, 1);

  const qualificationIds = useMemo(() => {
    if (qualificationsQuery.isSuccess && !qualificationsQuery.isLoading) {
      return qualificationsQuery.data.data.data.map((qualification) =>
        parseInt(qualification.id)
      );
    }
    return [];
  }, [
    qualificationsQuery.isSuccess,
    qualificationsQuery.isLoading,
    qualificationsQuery.data,
  ]);

  const qualificationQueries = useGetQualificationsByIds(
    qualificationIds,
    qualificationIds.length > 0,
    byQuestionId
  );

  const isLoading =
    qualificationsQuery.isIdle ||
    qualificationsQuery.isLoading ||
    qualificationQueries.some((query) => query.isLoading || query.isIdle);

  const isSuccess = qualificationQueries.every((query) => query.isSuccess);

  const data = useMemo(() => {
    if (!isLoading && isSuccess) {
      // discard qualifications without answers or custom answers
      const qualificationsWithAnswers = qualificationQueries.reduce(
        (acc, query) => {
          const qualification = (
            query.data as {
              data: { data: QualificationWithQualificationAnswers };
            }
          ).data.data as QualificationWithQualificationAnswers;

          if (
            qualification.attributes.qualification_answers.length > 0 ||
            qualification.attributes.custom_qualification_answers?.length > 0
          ) {
            acc.push(qualification);
          }

          return acc;
        },
        []
      );

      return qualificationsWithAnswers;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isSuccess]);

  return { isLoading, data };
};

export const useGetQualificationsByIds = (
  ids: number[],
  enabled = true,
  byQuestionId = false
) => {
  return useQueries(
    ids.map((id) => ({
      queryKey: [CACHE_KEYS.QUALIFICATION, id, byQuestionId],
      queryFn: () => {
        const query = byQuestionId
          ? {
              by_question_id: byQuestionId,
            }
          : ({} as StringifiableRecord);

        const urlWithQueryParams = queryString.stringifyUrl(
          {
            url: `/insights_platform/qualifications/${id}`,
            query,
          },
          { arrayFormat: 'bracket' }
        );

        return api.insightsPlatform.qualificationsDetail(
          `${id}`,
          byQuestionId,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          { path: urlWithQueryParams } as any
        );
      },
      enabled,
      select: (queryData: {
        data: InsightsPlatform.QualificationsDetail.ResponseBody;
      }) => {
        (
          queryData.data.data as QualificationWithQualificationAnswers
        ).attributes['qualification_answers'] = queryData.data.included;

        return queryData;
      },
      refetchOnWindowFocus: false,
    }))
  );
};
